import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useUnmount } from '@/components/admin/sessions/review/use-unmount';
import { useFloatingPlacement } from '@/lib/use-floating-placement';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * The floating box of the review (relecture.md §5.2, §5.3, §7): the list of
 * cases a selection opens, the popover of a passage already ranged, the form of
 * an annotation, and the one-sentence refusal of a selection across two messages.
 *
 * ## Where it stands
 *
 * Pointed at what opened it - a selection, a mark, a button - by the placer every
 * floating panel of this package shares (`useFloatingPlacement`): in a portal, in
 * `position: fixed`, flipped at the edge of the window, followed as the page
 * scrolls. A selection is not an element, so the anchor is a stand-in that answers
 * `getBoundingClientRect` from the live range - the one thing the placer asks of
 * it - and a mark or a button passes itself.
 *
 * ## How it closes
 *
 * Escape, a press anywhere outside it, and the gesture it was opened for. Focus
 * goes to its first control when it opens and back to where it came from when it
 * closes, so a keyboard user who chose a case is where they were.
 */

/** Where a popover points: a live rectangle, or the element itself. */
export type AnchorSource = HTMLElement | { readonly rect: () => DOMRect | null };

const NOWHERE = { left: 0, top: 0, width: 0, height: 0 } as const;

/**
 * An element that answers the placer's only question. The cast is the whole
 * adapter: `useFloatingPlacement` is written for an `HTMLElement` and asks it
 * for `getBoundingClientRect` and nothing else, and a selection has no element
 * to hand it.
 */
function anchorElement(source: AnchorSource): HTMLElement {
  if (source instanceof HTMLElement) return source;
  const stand = { getBoundingClientRect: () => source.rect() ?? NOWHERE };
  return stand as unknown as HTMLElement;
}

export interface ReviewPopoverProps {
  readonly kind: 'range' | 'placed' | 'annotate' | 'refusal';
  /** The accessible name of the dialog. */
  readonly label: string;
  readonly anchor: AnchorSource;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

export function ReviewPopover({ kind, label, anchor, onClose, children }: ReviewPopoverProps) {
  const target = useMemo(() => anchorElement(anchor), [anchor]);
  const { at, panelRef } = useFloatingPlacement({
    anchor: target,
    width: null,
    side: 'bottom',
    align: 'start',
  });
  const box = useRef<HTMLDialogElement | null>(null);
  const opener = useRef<Element | null>(document.activeElement);
  const attach = useCallback(
    (node: HTMLDialogElement | null) => {
      box.current = node;
      panelRef(node);
    },
    [panelRef],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onPress = (event: PointerEvent): void => {
      if (event.target instanceof Node && box.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [onClose]);

  // The first control takes the focus: the keyboard's way into the list.
  useEffect(() => {
    box.current
      ?.querySelector<HTMLElement>('button:not(:disabled)')
      ?.focus({ preventScroll: true });
  }, []);

  // Focus returns to where the popover came from - unless that is gone.
  useUnmount(() => {
    const back = opener.current;
    if (back instanceof HTMLElement && back.isConnected && back !== document.body) {
      back.focus({ preventScroll: true });
    }
  });

  return createPortal(
    <dialog
      open
      ref={attach}
      aria-label={label}
      data-testid={REVIEW_TEST_IDS.popover}
      data-kind={kind}
      style={{ left: at.left, top: at.top }}
      className="fixed right-auto bottom-auto z-50 m-0 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2 rounded-lg border border-gray-300 bg-white p-2.5 text-caption shadow-md"
    >
      {children}
    </dialog>,
    document.body,
  );
}
